/**
 * 工作区概况组件 - 三栏布局
 *
 * 展示工作区的核心信息，占满整个内容区域：
 * - 左侧：封面图片（保持与作者中心封面比例一致 200:260）
 * - 中间：作品简介、字数进度、章节进度、Token消耗
 * - 右侧：知识图谱
 *
 * 布局设计：左中右三栏布局，整体全屏展示
 */

import React, { useEffect, useState, useCallback } from 'react';
import { BookOpen, Calendar, PenTool, Coins, Target, TrendingUp, Edit2, Check, X, Save, RefreshCw } from 'lucide-react';
import type { Workspace } from '../../types/workspace';
import { workspaceApi } from '../../services/workspaceApi';
import { formatTokens } from '../../utils/formatters';
import { SimpleGraphViewer } from '../knowledge/SimpleGraphViewer';

// ==================== 类型定义 ====================

interface WorkspaceOverviewProps {
  workspace: Workspace;
  onWorkspaceUpdate?: (updatedWorkspace: Workspace) => void;
}

// ==================== 辅助函数 ====================

/**
 * 获取分类显示名称
 */
const getCategoryLabel = (category: string): string => {
  return category === 'male' ? '男频' : '女频';
};

/**
 * 计算创作天数
 */
const calculateCreationDays = (createdAt: string): number => {
  const created = new Date(createdAt);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - created.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * 格式化字数显示
 */
const formatWordCount = (count: number): string => {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万`;
  }
  return count.toLocaleString();
};

// ==================== 子组件 ====================

/**
 * 统计卡片组件
 */
interface StatsCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subValue?: string;
  colorClass: string;
  highlight?: boolean;
}

const StatsCard: React.FC<StatsCardProps> = ({
  icon,
  label,
  value,
  subValue,
  colorClass,
  highlight = false,
}) => {
  return (
    <div
      className={`
        relative overflow-hidden rounded-xl p-4 transition-all duration-150
        ${highlight
          ? 'bg-gray-800 text-white shadow-sm'
          : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md'
        }
      `}
    >
      {highlight && (
        <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/5 rounded-full" />
      )}

      <div className="relative flex items-start gap-3">
        <div
          className={`
            p-2.5 rounded-lg flex-shrink-0
            ${highlight ? 'bg-white/10' : `${colorClass} bg-opacity-10`}
          `}
        >
          {React.cloneElement(icon as React.ReactElement, {
            className: `w-5 h-5 ${highlight ? 'text-white' : colorClass.replace('bg-', 'text-')}`
          })}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-medium ${highlight ? 'text-gray-300' : 'text-gray-500'}`}>
            {label}
          </p>
          <p className={`text-lg font-bold mt-0.5 ${highlight ? 'text-white' : 'text-gray-800'}`}>
            {value}
          </p>
          {subValue && (
            <p className={`text-xs mt-0.5 ${highlight ? 'text-gray-400' : 'text-gray-400'}`}>
              {subValue}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * 进度条组件
 */
interface ProgressSectionProps {
  label: string;
  current: number;
  target: number;
  unit: string;
  colorClass: string;
}

const ProgressSection: React.FC<ProgressSectionProps> = ({
  label,
  current,
  target,
  unit,
  colorClass,
}) => {
  // 字数进度：将当前字数转为万字计算，目标已经是万字
  // 章节进度：直接使用数值
  const currentValue = unit === '字' ? current / 10000 : current;
  const targetValue = unit === '字' ? target : target;
  const percentage = Math.min(Math.round((currentValue / targetValue) * 100), 100);

  // 格式化显示
  const formatCurrent = () => {
    if (unit === '字') {
      // 字数显示为万字（保留1位小数）
      return `${(current / 10000).toFixed(1)}万字`;
    }
    // 章节显示为X章
    return `${current}章`;
  };

  const formatTarget = () => {
    if (unit === '字') {
      return `${target}万字`;
    }
    return `${target}章`;
  };

  return (
    <div className="group bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-all duration-150">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-gray-400" />
          <h3 className="font-semibold text-gray-800">{label}</h3>
        </div>
        <span className="text-2xl font-bold text-gray-800">
          {percentage}%
        </span>
      </div>

      <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden mb-3">
        <div
          className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${colorClass}`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">
          当前: <span className="font-medium text-gray-700">{formatCurrent()}</span>
        </span>
        <span className="text-gray-500">
          目标: <span className="font-medium text-gray-700">{formatTarget()}</span>
        </span>
      </div>
    </div>
  );
};

// ==================== 主组件 ====================

/**
 * 工作区概况组件 - 三栏布局
 * 
 * 布局结构：
 * - 左侧（3列）：封面图片，保持 200:260 比例与作者中心一致
 * - 中间（5列）：作品简介、字数进度、章节进度、Token消耗
 * - 右侧（4列）：知识图谱
 */
export const WorkspaceOverview: React.FC<WorkspaceOverviewProps> = ({ 
  workspace,
  onWorkspaceUpdate 
}) => {
  const [tokenStats, setTokenStats] = useState<number>(0);
  const [tokenLoading, setTokenLoading] = useState(true);
  
  // 简介编辑状态
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [summaryText, setSummaryText] = useState(workspace.summary || '');
  const [isSavingSummary, setIsSavingSummary] = useState(false);
  
  // 图谱懒加载状态
  const [showGraph, setShowGraph] = useState(false);
  const [graphRefreshKey, setGraphRefreshKey] = useState(0);

  // 加载 Token 统计数据
  useEffect(() => {
    const loadTokenStats = async () => {
      try {
        const totalTokens = await workspaceApi.getTokenStats(undefined, workspace.id);
        setTokenStats(totalTokens);
      } catch (error) {
        console.error('Failed to load token stats:', error);
        setTokenStats(workspace.tokens_consumed || 0);
      } finally {
        setTokenLoading(false);
      }
    };

    loadTokenStats();
  }, [workspace.id, workspace.tokens_consumed]);

  // 图谱懒加载：延迟1秒后显示图谱
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowGraph(true);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // 同步外部 workspace 变化
  useEffect(() => {
    setSummaryText(workspace.summary || '');
  }, [workspace.summary]);

  // 保存简介
  const handleSaveSummary = useCallback(async () => {
    if (summaryText === workspace.summary) {
      setIsEditingSummary(false);
      return;
    }

    setIsSavingSummary(true);
    try {
      const updatedWorkspace = await workspaceApi.updateWorkspace(
        undefined,
        workspace.id,
        { summary: summaryText }
      );
      onWorkspaceUpdate?.(updatedWorkspace);
      setIsEditingSummary(false);
    } catch (error) {
      console.error('Failed to update summary:', error);
      alert('保存简介失败，请重试');
    } finally {
      setIsSavingSummary(false);
    }
  }, [summaryText, workspace.id, workspace.summary, onWorkspaceUpdate]);

  // 取消编辑
  const handleCancelEdit = useCallback(() => {
    setSummaryText(workspace.summary || '');
    setIsEditingSummary(false);
  }, [workspace.summary]);

  // 计算统计数据
  const creationDays = calculateCreationDays(workspace.created_at);

  // 按天的 Token 统计数据
  const [dailyTokenStats, setDailyTokenStats] = useState<Array<{
    date: string;
    tokens: number;
    request_count: number;
  }>>([]);
  const [dailyStatsLoading, setDailyStatsLoading] = useState(true);

  // 加载按天 Token 统计
  useEffect(() => {
    const loadDailyTokenStats = async () => {
      try {
        setDailyStatsLoading(true);
        const response = await workspaceApi.getDailyTokenStats(undefined, workspace.id, 7);
        setDailyTokenStats(response.daily_stats);
      } catch (error) {
        console.error('Failed to load daily token stats:', error);
      } finally {
        setDailyStatsLoading(false);
      }
    };

    loadDailyTokenStats();
  }, [workspace.id]);

  // 格式化日期为星期几
  const getDayLabel = (dateStr: string): string => {
    const date = new Date(dateStr);
    const days = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    return days[date.getDay()];
  };

  const maxDailyValue = Math.max(...dailyTokenStats.map(d => d.tokens), 1);

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-[1600px] mx-auto p-6">
        {/* 头部标题 */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-sm">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">作品概况</h2>
            <p className="text-xs text-gray-500">实时追踪创作进度与数据</p>
          </div>
        </div>

        {/* 主要内容区域 - 左中右三栏布局 */}
        <div className="grid grid-cols-12 gap-6 items-stretch">
          {/* 左侧：封面（3列）- 保持与作者中心封面比例一致 200:260 */}
          <div className="col-span-3">
            <div className="group bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-150 h-full flex flex-col">
              {/* 封面区域 - 使用 aspect-[200/260] 保持与 WorkspaceCard 一致的比例 */}
              <div className="relative bg-gray-100 overflow-hidden aspect-[200/260] flex-shrink-0">
                {workspace.cover_path ? (
                  <img
                    src={workspace.cover_path}
                    alt={`${workspace.name} 封面`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                    <BookOpen className="w-16 h-16 mb-3 opacity-50" />
                    <span className="text-sm">暂无封面</span>
                  </div>
                )}

                {/* 分类标签 */}
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className="px-2.5 py-1 bg-emerald-500 text-white rounded-full text-xs font-medium shadow-md">
                    {getCategoryLabel(workspace.category)}
                  </span>
                </div>
                <div className="absolute top-3 right-3">
                  <span className="px-2.5 py-1 bg-white text-gray-700 rounded-full text-xs font-medium shadow-sm">
                    {workspace.genre}
                  </span>
                </div>
              </div>

              {/* 作品名称和创建信息 */}
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">{workspace.name}</h3>
                <div className="mt-auto pt-3 border-t border-gray-100 text-xs text-gray-400 space-y-1">
                  <div className="flex justify-between">
                    <span>创建于</span>
                    <span>{new Date(workspace.created_at).toLocaleDateString('zh-CN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>最后更新</span>
                    <span>{new Date(workspace.updated_at).toLocaleDateString('zh-CN')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 中间：作品简介、进度、Token消耗（5列） */}
          <div className="col-span-5 flex flex-col gap-4">
            {/* 作品简介 - 支持编辑 */}
            <div className="group bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-all duration-150 flex-1">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-gray-400" />
                  作品简介
                </h3>
                {!isEditingSummary ? (
                  <button
                    onClick={() => setIsEditingSummary(true)}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    编辑
                  </button>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={handleCancelEdit}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                      disabled={isSavingSummary}
                    >
                      <X className="w-3.5 h-3.5" />
                      取消
                    </button>
                    <button
                      onClick={handleSaveSummary}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors disabled:opacity-50"
                      disabled={isSavingSummary}
                    >
                      {isSavingSummary ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          保存中
                        </>
                      ) : (
                        <>
                          <Save className="w-3.5 h-3.5" />
                          保存
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {isEditingSummary ? (
                <textarea
                  value={summaryText}
                  onChange={(e) => setSummaryText(e.target.value)}
                  placeholder="请输入作品简介..."
                  className="w-full h-[120px] p-3 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                />
              ) : (
                <div className="bg-gray-50 rounded-lg p-3 min-h-[120px]">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {workspace.summary || '暂无作品简介，点击编辑按钮添加...'}
                  </p>
                </div>
              )}
            </div>

            {/* 创作进度 - 字数和章节并排 */}
            <div className="grid grid-cols-2 gap-4">
              {/* 字数进度 */}
              <ProgressSection
                label="字数进度"
                current={workspace.created_words}
                target={workspace.target_words}
                unit="字"
                colorClass="bg-blue-500"
              />
              {/* 章节进度 */}
              <ProgressSection
                label="章节进度"
                current={workspace.created_chapters}
                target={workspace.total_chapters}
                unit="章"
                colorClass="bg-purple-500"
              />
            </div>

            {/* Token消耗 */}
            <div className="group bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-all duration-150">
              {/* 标题和总量 */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-50 rounded-lg">
                    <Coins className="w-4 h-4 text-blue-500" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">Token 消耗</span>
                </div>
                <span className="text-base font-bold text-gray-800">
                  {tokenLoading ? '...' : formatTokens(tokenStats)}
                </span>
              </div>

              {/* 柱状图 */}
              <div className="flex items-end justify-between gap-2 h-[100px]">
                {dailyStatsLoading ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin" />
                  </div>
                ) : dailyTokenStats.every(item => item.tokens === 0) ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <p className="text-xs text-gray-400">暂无 Token 消耗数据</p>
                  </div>
                ) : (
                  dailyTokenStats.map((item, index) => {
                    const heightPercent = maxDailyValue > 0 ? (item.tokens / maxDailyValue) * 90 : 0;
                    return (
                      <div key={index} className="flex flex-col items-center flex-1 h-full">
                        <div className="w-full flex items-end justify-center flex-1">
                          <div
                            className="w-full max-w-[32px] bg-blue-500 rounded-t-sm transition-all duration-500 hover:bg-blue-600 relative group"
                            style={{ height: `${heightPercent}%` }}
                          >
                            <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] py-0.5 px-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                              {formatTokens(item.tokens)}
                            </div>
                          </div>
                        </div>
                        <div className="text-[10px] text-gray-400 mt-1 flex-shrink-0">{getDayLabel(item.date)}</div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* 底部说明 */}
              <div className="mt-2 pt-2 border-t border-gray-100">
                <p className="text-[10px] text-gray-400">近7天 Token 消耗趋势</p>
              </div>
            </div>
          </div>

          {/* 右侧：知识图谱（4列） */}
          <div className="col-span-4">
            <div className="group bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-all duration-150 h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-emerald-50 rounded-lg">
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">知识图谱</span>
                </div>
                <button
                  onClick={() => setGraphRefreshKey(prev => prev + 1)}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  刷新
                </button>
              </div>
              
              <div className="relative flex-1 min-h-[400px]">
                {showGraph ? (
                  <SimpleGraphViewer
                    key={graphRefreshKey}
                    workspaceId={workspace.id}
                    height={400}
                    compact={false}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin" />
                  </div>
                )}
              </div>

              {/* 底部说明 */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-400">展示项目实体关系网络概览</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceOverview;
